import { useCallback, useEffect, useState } from 'react';

import type { UserInfo, UserRole } from '../hooks/auth-context';
import { useAuth } from '../hooks/useAuth';
import { fetchUsers, updateUserRole } from '../utils/api';

export function UserManagementPage() {
  const { user: currentUser, refreshUser } = useAuth();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchUsers();
      setUsers(
        [...result].sort((left, right) =>
          (left.email ?? '').localeCompare(right.email ?? '')
        )
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const changeRole = async (account: UserInfo, role: UserRole) => {
    if (role === account.role || !account.id) return;

    setUpdatingId(account.id);
    setError(null);
    setMessage(null);
    try {
      const updated = await updateUserRole(account.id, role);
      setUsers((current) =>
        current.map((entry) => (entry.id === updated.id ? updated : entry))
      );
      setMessage(`${updated.email} is now ${updated.role}.`);
      if (updated.id === currentUser?.id) {
        await refreshUser();
      }
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Failed to update the user role'
      );
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">User management</h1>
          <p className="mt-1 text-sm text-slate-500">
            Assign administrator access or keep accounts in read-only mode.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadUsers()}
          disabled={loading}
          className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {error}
        </div>
      )}
      {message && (
        <div
          role="status"
          className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700"
        >
          {message}
        </div>
      )}

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-4 sm:px-5">
          <h2 className="font-semibold text-slate-800">Accounts</h2>
          <p className="mt-1 text-xs text-slate-500">
            Regular accounts can view everything but cannot kill running jobs,
            delete history entries or delete background images. Administrators
            can do all of that and manage roles here.
          </p>
        </div>

        {loading ? (
          <div
            className="flex min-h-48 items-center justify-center"
            role="status"
            aria-live="polite"
          >
            <span
              className="h-7 w-7 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600"
              aria-hidden="true"
            />
            <span className="sr-only">Loading users</span>
          </div>
        ) : users.length === 0 ? (
          <p className="px-5 py-12 text-center text-sm text-slate-500">
            No users found. Accounts appear here the first time they sign in.
          </p>
        ) : (
          <div className="p-3 sm:p-0">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="px-5 py-3">User</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Role</th>
                </tr>
              </thead>
              <tbody>
                {users.map((account) => {
                  const updating = updatingId === account.id;
                  const isCurrent = account.id === currentUser?.id;
                  return (
                    <tr key={account.id}>
                      <td className="px-5 py-3" data-label="User">
                        <span className="flex items-center gap-2 text-slate-800">
                          <span>{account.name || 'Unnamed user'}</span>
                          {isCurrent && (
                            <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600">
                              Current
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-600" data-label="Email">
                        <span className="break-all">{account.email}</span>
                      </td>
                      <td className="px-5 py-3" data-label="Role">
                        <select
                          aria-label={`Role for ${account.email}`}
                          value={account.role ?? 'regular'}
                          disabled={updating}
                          onChange={(event) =>
                            void changeRole(account, event.target.value as UserRole)
                          }
                          className="min-w-28 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium capitalize text-slate-700 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 disabled:cursor-wait disabled:opacity-60"
                        >
                          <option value="regular">Regular</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
